import { useState } from 'react';
import { TextInput, Select, Textarea } from '@mantine/core';
import { IconTrash, IconPencil, IconCheck, IconX } from '@tabler/icons-react';
import type { AllergyLocal, AllergySeverity, AllergyCategory } from '../../types/medications';
import { SEVERITY_OPTIONS, ALLERGY_CATEGORY_OPTIONS } from '../../types/medications';

interface AllergyFormProps {
  allergy: AllergyLocal;
  onChange: (updated: AllergyLocal) => void;
  onRemove: () => void;
  onSubmit: () => void;
  disabled?: boolean;
  isNew?: boolean;
}

// Severity badge styling
function getSeverityBadgeClass(severity: AllergySeverity | undefined): string {
  switch (severity) {
    case 'mild':
      return 'bg-yellow-500/20 text-yellow-400';
    case 'moderate':
      return 'bg-orange-500/20 text-orange-400';
    case 'severe':
      return 'bg-red-500/20 text-red-400';
    default:
      return 'bg-gray-500/20 text-gray-400';
  }
}

function getSeverityLabel(severity: AllergySeverity | undefined): string {
  switch (severity) {
    case 'mild':
      return 'Mild';
    case 'moderate':
      return 'Moderate';
    case 'severe':
      return 'Severe';
    default:
      return '';
  }
}

export function AllergyForm({
  allergy,
  onChange,
  onRemove,
  onSubmit,
  disabled = false,
  isNew = false,
}: AllergyFormProps) {
  const [isEditing, setIsEditing] = useState(isNew);

  const handleChange = (
    field: keyof AllergyLocal,
    value: string | AllergySeverity | AllergyCategory | undefined
  ) => {
    onChange({ ...allergy, [field]: value });
  };

  const handleSave = () => {
    if (allergy.allergyName.trim()) {
      onSubmit();
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    if (isNew) {
      onRemove();
    } else {
      setIsEditing(false);
    }
  };

  // View mode - display as card
  if (!isEditing) {
    return (
      <div className="card p-4 group">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-gray-200">{allergy.allergyName}</h4>
              {allergy.category && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                  {ALLERGY_CATEGORY_OPTIONS.find(o => o.value === allergy.category)?.label || allergy.category}
                </span>
              )}
              {allergy.severity && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${getSeverityBadgeClass(allergy.severity)}`}>
                  {getSeverityLabel(allergy.severity)}
                </span>
              )}
            </div>
            <div className="mt-1 text-sm text-gray-400 space-y-0.5">
              {allergy.reaction && (
                <p>Reaction: {allergy.reaction}</p>
              )}
              {allergy.comments && (
                <p className="text-gray-500 italic">{allergy.comments}</p>
              )}
            </div>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              type="button"
              onClick={() => setIsEditing(true)}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors disabled:opacity-50"
              title="Edit"
            >
              <IconPencil size={16} />
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={disabled}
              className="p-1.5 rounded-lg text-gray-400 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
              title="Delete"
            >
              <IconTrash size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  return (
    <div className="card p-4 border-cyan-500/30">
      <div className="space-y-4">
        {/* Allergy Name */}
        <TextInput
          label="Allergy or Sensitivity"
          placeholder="e.g., Penicillin, Peanuts, Latex, Gluten"
          value={allergy.allergyName}
          onChange={(e) => handleChange('allergyName', e.target.value)}
          disabled={disabled}
          required
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Category */}
          <Select
            label="Category"
            placeholder="Select category"
            data={ALLERGY_CATEGORY_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
            value={allergy.category || null}
            onChange={(val) =>
              handleChange('category', (val as AllergyCategory) || undefined)
            }
            disabled={disabled}
            clearable
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
              dropdown: 'bg-gray-900 border-gray-700',
              option: 'hover:bg-gray-800 text-gray-200',
            }}
          />

          {/* Reaction */}
          <TextInput
            label="Reaction"
            placeholder="e.g., Hives, Difficulty breathing"
            value={allergy.reaction || ''}
            onChange={(e) => handleChange('reaction', e.target.value || undefined)}
            disabled={disabled}
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
            }}
          />

          {/* Severity */}
          <Select
            label="Severity"
            placeholder="Select severity"
            data={SEVERITY_OPTIONS.map(o => ({ value: o.value, label: o.label }))}
            value={allergy.severity || null}
            onChange={(val) =>
              handleChange('severity', (val as AllergySeverity) || undefined)
            }
            disabled={disabled}
            clearable
            classNames={{
              input: 'input',
              label: 'text-sm font-medium text-gray-300 mb-1',
              dropdown: 'bg-gray-900 border-gray-700',
              option: 'hover:bg-gray-800 text-gray-200',
            }}
          />
        </div>

        {/* Comments */}
        <Textarea
          label="Additional Details"
          placeholder="Any other details: when discovered, treatment needed, etc."
          value={allergy.comments || ''}
          onChange={(e) => handleChange('comments', e.target.value || undefined)}
          disabled={disabled}
          minRows={2}
          classNames={{
            input: 'input',
            label: 'text-sm font-medium text-gray-300 mb-1',
          }}
        />

        {/* Action buttons */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={handleCancel}
            disabled={disabled}
            className="px-3 py-1.5 text-sm text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            <IconX size={14} />
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={disabled || !allergy.allergyName.trim()}
            className="px-3 py-1.5 text-sm bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <IconCheck size={14} />
            {isNew ? 'Add Allergy' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
